import {
  IconButton,
  Menu,
  MenuItem,
  MenuList,
  Stack,
  Typography,
  Dialog,
  AppBar,
  Toolbar,
  Button,
  DialogContent,
  Grid2,
  Backdrop,
  CircularProgress,
} from '@mui/material'
import PopupState, { bindMenu, bindTrigger } from 'material-ui-popup-state'
import { PopupState as PopupStateType } from 'material-ui-popup-state/hooks'
import { Fragment } from 'react/jsx-runtime'
import {
  ContentCut,
  DriveFileMove,
  FileDownloadRounded,
  MoreVertRounded,
  PersonOutlineRounded,
  RemoveRedEyeRounded,
} from '@mui/icons-material'
import {
  TimepointContainerSubmissionExamMetadata,
  TimepointContainerSubmissionStatus,
  TimepointSubmissionStatus,
} from '@common/config/api/client'
import { SPACING } from '@common/theme/spacing'
import { SIZING } from '@common/theme/sizing'
import { useEffect, useState } from 'react'
import SeriesModal from '@features/qc_task_details_view/pages/modals/SeriesModal'
import { ExamReassignementProvider } from '@features/qc_task_details_view/contexts/ExamReassignementProvider'
import ReassignExamModal from '@features/qc_task_details_view/pages/modals/ReassignExamModal'
import { ContainerParam } from '@features/subject/subject.types'
import { launchDicomViewerForSite } from '@common/utils/ohifUtils'
import { useExtendedAuth } from '@auth/hooks/useExtendedAuth'
import { useParams } from 'react-router-dom'
import { SitePermission, TimepointPermission } from '@auth/permissionsEnum'
import { useCombinedPermissions } from '@auth/hooks/useCombinedPermissions'
import PHIRedactionStep from '../PHIRedactionStep'
import { useDownloadExamMetadata } from '@features/subject/hooks/useDownloadExamMetadata'
import { toast } from 'sonner'
import type { DownloadExamMetadataParams } from '@features/subject/hooks/useDownloadExamMetadata'
import { NonDicomViewerModal } from '@features/nonDicomViewer'
import { useGetTimepointSubmissionUploader } from '@features/subject/hooks/useGetTimepointSubmissionUploader'
import UploaderDetailsModal from '@common/components/UploaderDetailsModal'
import {
  CriteriaCheckResultsDialog,
  CriteriaCheckResultsMenuItem,
} from '@features/subject/components/CriteriaCheckResultsMenuItem'

export default function ExamDetailPopupMenuList({
  container_submission_id,
  containers,
  subject_pseudo_id,
  exam,
  timepointSubmissionStatus,
  isDicom = true,
}: {
  container_submission_id?: string
  subject_pseudo_id?: string
  containers?: ContainerParam[]
  exam?: TimepointContainerSubmissionExamMetadata
  timepointSubmissionStatus?: TimepointSubmissionStatus | TimepointContainerSubmissionStatus
  isDicom?: boolean
}) {
  const { studyId, timepointSubmissionId, subjectId } = useParams()
  const [loading, setLoading] = useState(false)
  const [reassignModalOpen, setReassignModalOpen] = useState(false)
  const [phiRedactionOpen, setPhiRedactionOpen] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [uploaderModalOpen, setUploaderModalOpen] = useState(false)
  const [criteriaCheckResultsOpen, setCriteriaCheckResultsOpen] = useState(false)
  const auth = useExtendedAuth()
  const { hasPermission } = useCombinedPermissions()
  const downloadExamMetadata = useDownloadExamMetadata()
  const {
    data: uploaderData,
    isLoading: uploaderLoading,
    isError: uploaderError,
  } = useGetTimepointSubmissionUploader(
    {
      study_id: studyId,
      subject_id: subjectId,
      timepoint_submission_id: timepointSubmissionId,
    },
    uploaderModalOpen,
  )

  // Safely access user with optional chaining
  const user = auth?.user

  const handledDownloadExamMetadata = async (popupState: PopupStateType) => {
    // Always close the menu first, regardless of whether download proceeds
    popupState.close()

    // Return early if any required parameters are missing
    if (!container_submission_id || !studyId || !subjectId || !timepointSubmissionId || !user?.access_token) {
      return
    }

    // Show loading state
    setLoading(true)
    toast.loading('Preparing download...', { id: 'exam-download' })

    try {
      // Create parameters object with required fields
      const params: DownloadExamMetadataParams = {
        study_id: studyId,
        subject_id: subjectId,
        timepoint_submission_id: timepointSubmissionId,
        container_submission_id: container_submission_id,
      }

      // Add dicom_study_uid only if it exists
      if (exam?.study_uid) {
        params.dicom_study_uid = exam.study_uid
      }

      const response = await downloadExamMetadata.mutateAsync(params)

      // Create and click a temporary download link to trigger the file download
      if (response.url) {
        try {
          const fileResponse = await fetch(response.url)
          const blob = await fileResponse.blob()
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.download = response.url.split('/').pop()?.split('?')[0] || 'exam_metadata.zip'
          document.body.appendChild(link)
          link.click()
          window.URL.revokeObjectURL(url)
          document.body.removeChild(link)
          toast.success('Download started successfully', { id: 'exam-download' })
        } catch (error) {
          console.error('Error downloading the file:', error)
          toast.error('Error downloading the file', { id: 'exam-download' })
        }
      }
    } catch (error) {
      console.error('Error fetching download URL:', error)
      toast.error('Error generating download URL', { id: 'exam-download' })
    } finally {
      setLoading(false)
    }
  }

  const handlePhiRedactionClose = () => {
    setPhiRedactionOpen(false)
  }

  useEffect(() => {
    const reassignModalState = sessionStorage.getItem('reassign_exam_modal_state')
    if (reassignModalState) {
      try {
        const modalState = JSON.parse(reassignModalState)
        console.log('modalState', modalState)
        if (modalState.isEsignModalOpen) {
          setReassignModalOpen(true)
        }
      } catch (error) {
        console.error('Error parsing reassign modal state:', error)
      }
    }
  }, [])

  return (
    <PopupState variant="popover">
      {(popupState: PopupStateType) => (
        <Fragment>
          <IconButton {...bindTrigger(popupState)}>
            <MoreVertRounded
              sx={{
                color: 'text.secondary',
                width: 20,
                height: 20,
              }}
            />
          </IconButton>
          <Menu
            {...bindMenu(popupState)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            data-testid="popup-menu-list"
          >
            <MenuList
              sx={{
                width: 220,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                paddingY: SPACING.examDetailPopupMenuListSpacing,
                outline: 'none',
              }}
            >
              {isDicom && (
                <MenuItem
                  sx={{
                    height: 36,
                    width: '100%',
                    paddingY: SPACING.examDetailPopupMenuListSpacing,
                  }}
                  onClick={() => {
                    popupState.close()
                    if (
                      studyId &&
                      subjectId &&
                      timepointSubmissionId &&
                      container_submission_id &&
                      user?.access_token
                    ) {
                      launchDicomViewerForSite(
                        studyId,
                        subjectId,
                        timepointSubmissionId,
                        container_submission_id,
                        user.access_token,
                        undefined,
                        false,
                        exam?.study_uid || undefined,
                        Boolean(exam?.study_uid),
                      )
                    }
                  }}
                  data-testid="popup-menu-list-item-reopen"
                >
                  <Stack direction="row" spacing={SPACING.popupMenuIconSpacing} alignItems="center">
                    <RemoveRedEyeRounded
                      sx={{
                        color: 'text.secondary',
                      }}
                    />
                    <Typography>Open in viewer</Typography>
                  </Stack>
                </MenuItem>
              )}
              {isDicom && (
                <MenuItem
                  sx={{
                    height: 36,
                    width: '100%',
                    paddingY: SPACING.examDetailPopupMenuListSpacing,
                  }}
                  data-testid="popup-menu-list-item-reset"
                >
                  <SeriesModal
                    container_submission_id={container_submission_id}
                    exam={exam}
                    popupState={popupState}
                    timepointSubmissionStatus={timepointSubmissionStatus}
                  />
                </MenuItem>
              )}
              {isDicom && container_submission_id && studyId && subjectId && timepointSubmissionId && (
                <CriteriaCheckResultsMenuItem
                  onClick={() => {
                    setCriteriaCheckResultsOpen(true)
                    popupState.close()
                  }}
                />
              )}
              {isDicom &&
                (hasPermission(TimepointPermission.ALL) || hasPermission(TimepointPermission.REASSIGN_EXAM)) && (
                  <MenuItem
                    sx={{
                      height: 36,
                      width: '100%',
                      paddingY: SPACING.examDetailPopupMenuListSpacing,
                    }}
                    onClick={() => {
                      setReassignModalOpen(true)
                      popupState.close()
                    }}
                    data-testid="popup-menu-list-item-reassign"
                  >
                    <Stack direction="row" spacing={SPACING.popupMenuIconSpacing} alignItems="center">
                      <DriveFileMove
                        sx={{
                          color: 'text.secondary',
                        }}
                      />
                      <Typography>Reassign exam</Typography>
                    </Stack>
                  </MenuItem>
                )}
              {!isDicom && (
                <MenuItem
                  sx={{
                    height: 36,
                    width: '100%',
                    paddingY: SPACING.examDetailPopupMenuListSpacing,
                  }}
                  onClick={() => {
                    popupState.close()
                    setPreviewOpen(true)
                  }}
                  data-testid="popup-menu-list-item-preview"
                >
                  <Stack direction="row" spacing={SPACING.popupMenuIconSpacing} alignItems="center">
                    <RemoveRedEyeRounded
                      sx={{
                        color: 'text.secondary',
                      }}
                    />
                    <Typography>Preview</Typography>
                  </Stack>
                </MenuItem>
              )}
              <MenuItem
                sx={{
                  height: 36,
                  width: '100%',
                  paddingY: SPACING.examDetailPopupMenuListSpacing,
                }}
                onClick={() => handledDownloadExamMetadata(popupState)}
                disabled={loading}
                data-testid="popup-menu-list-item-download"
              >
                <Stack direction="row" spacing={SPACING.popupMenuIconSpacing} alignItems="center">
                  <FileDownloadRounded
                    sx={{
                      color: 'text.secondary',
                    }}
                  />
                  <Typography>Download</Typography>
                </Stack>
              </MenuItem>
              <MenuItem
                sx={{
                  height: 36,
                  width: '100%',
                  paddingY: SPACING.examDetailPopupMenuListSpacing,
                }}
                onClick={() => {
                  setUploaderModalOpen(true)
                  popupState.close()
                }}
                data-testid="popup-menu-list-item-uploaded-by"
              >
                <Stack direction="row" spacing={SPACING.popupMenuIconSpacing} alignItems="center">
                  <PersonOutlineRounded
                    sx={{
                      color: 'text.secondary',
                    }}
                  />
                  <Typography>Uploaded by</Typography>
                </Stack>
              </MenuItem>
              {isDicom &&
                (hasPermission(SitePermission.ALL) || hasPermission(SitePermission.REDACT_PHI)) &&
                studyId &&
                subjectId &&
                timepointSubmissionId &&
                timepointSubmissionStatus !== 'LOCKED' &&
                timepointSubmissionStatus !== 'COMPLETE' && (
                  <MenuItem
                    sx={{
                      height: 36,
                      width: '100%',
                      paddingY: SPACING.examDetailPopupMenuListSpacing,
                    }}
                    onClick={() => {
                      setPhiRedactionOpen(true)
                      popupState.close()
                    }}
                    data-testid="popup-menu-list-item-phi-redaction"
                  >
                    <Stack direction="row" spacing={SPACING.popupMenuIconSpacing} alignItems="center">
                      <ContentCut
                        sx={{
                          color: 'text.secondary',
                        }}
                      />
                      <Typography>Redact PHI</Typography>
                    </Stack>
                  </MenuItem>
                )}
            </MenuList>
          </Menu>

          {/* Global loading backdrop */}
          <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
            <CircularProgress color="inherit" />
          </Backdrop>

          <UploaderDetailsModal
            open={uploaderModalOpen}
            onClose={() => setUploaderModalOpen(false)}
            data={uploaderData}
            isLoading={uploaderLoading}
            isError={uploaderError}
          />

          {container_submission_id && studyId && subjectId && timepointSubmissionId && (
            <CriteriaCheckResultsDialog
              open={criteriaCheckResultsOpen}
              onClose={() => setCriteriaCheckResultsOpen(false)}
              fetchParams={{
                mode: 'site',
                studyId,
                subjectId,
                timepointSubmissionId,
                containerSubmissionId: container_submission_id,
              }}
            />
          )}

          {reassignModalOpen && (
            <ExamReassignementProvider>
              <ReassignExamModal
                subject_pseudo_id={subject_pseudo_id}
                containers={containers}
                popupState={{
                  ...popupState,
                  close: () => {
                    setReassignModalOpen(false)
                    sessionStorage.removeItem('reassign_exam_modal_state')
                    popupState.close()
                  },
                }}
              />
            </ExamReassignementProvider>
          )}

          <Dialog
            fullScreen
            open={phiRedactionOpen}
            onClose={handlePhiRedactionClose}
            aria-labelledby="phi-redaction-dialog-title"
            aria-describedby="phi-redaction-dialog-description"
          >
            <AppBar position="static" sx={{ bgcolor: 'white', boxShadow: 2, zIndex: 10 }}>
              <Toolbar sx={{ justifyContent: 'space-between' }}>
                <Stack>
                  <Typography id="phi-redaction-dialog-title" color="text.primary" variant="subtitle2">
                    PHI Redaction
                  </Typography>
                  <Typography id="phi-redaction-dialog-description" color="text.secondary" variant="body2">
                    Subject {subject_pseudo_id}
                  </Typography>
                </Stack>
                <Button
                  onClick={handlePhiRedactionClose}
                  color="secondary"
                  variant="contained"
                  size="large"
                  aria-label="Close PHI redaction dialog"
                >
                  Close
                </Button>
              </Toolbar>
            </AppBar>
            <DialogContent sx={{ p: SPACING.noSpacing, bgcolor: '#F0F4F5' }}>
              <Grid2 container height={SIZING.minWidth}>
                <Grid2 size={SIZING.gridSizeXxl} pt={SPACING.spacingXxl}>
                  {studyId && subjectId && timepointSubmissionId && (
                    <PHIRedactionStep
                      studyId={studyId}
                      subjectId={subjectId}
                      timepointSubmissionId={timepointSubmissionId}
                    />
                  )}
                </Grid2>
              </Grid2>
            </DialogContent>
          </Dialog>

          {!isDicom && previewOpen && studyId && subjectId && timepointSubmissionId && container_submission_id && (
            <NonDicomViewerModal
              open={previewOpen}
              onClose={() => setPreviewOpen(false)}
              fileName={exam?.file_name}
              fileExtension={exam?.file_extension}
              examMetadataId={exam?.exam_metadata_id}
              studyId={studyId}
              subjectId={subjectId}
              timepointSubmissionId={timepointSubmissionId}
              containerSubmissionId={container_submission_id}
            />
          )}
        </Fragment>
      )}
    </PopupState>
  )
}
